// src/features/tickets/components/TicketReplyComposer.tsx
//
// Composer fuer Antworten und interne Notizen innerhalb eines Tickets.
// Ersetzt den Platzhalter-Block in TicketDetail.tsx.
//
// Zwei Modi:
//   - 'reply' (Antwort an Kunde): Empfaenger + CC + Body + Anhaenge,
//     ruft addMessage(is_internal_note=false, send_email=true) → Backend
//     versendet Mail und persistiert Anhaenge.
//   - 'note' (Interne Notiz): nur Body + Anhaenge, gelb markiert,
//     ruft addMessage(is_internal_note=true) → Frontend persistiert Anhaenge
//     via TicketService.attachToMessage.
//
// „Senden als andere Person": Im Antwort-Modus ist der Absender wählbar
// (SenderSelection, ohne Foto-Pflicht — die Ticket-Signatur hat einen
// Text-Fallback). Weicht der gewählte Absender vom Actor ab, geht sent_as an
// den Service: die Außenwirkung (From-Anzeigename, Signatur, Anrede/Duz-Gate
// der KI-Prüfung) folgt dem gewählten Absender; author_*/created_by_* bleiben
// die echte Person (Audit).
//
// Nach erfolgreichem Submit werden die Felder zurueckgesetzt und der
// onSent-Callback ausgeloest (Reload des Tickets im Parent).

import React, { useState } from 'react';
import { Reply, ReplyAll, StickyNote, Paperclip, X, Send, Loader, Clock } from 'lucide-react';
import { RichTextEmailEditor, prepareEmailHtml, isEmailHtmlEmpty } from './RichTextEmailEditor';
import { SenderSelection } from '../../deadlines/components/SenderSelection';
import { useSenderManagement } from '../../deadlines/hooks/useSenderManagement';
import { EmailSenderIdentity } from '../../../services/email/types';
import { ticketService } from '../services/TicketService';
import { TicketActor, TicketAttachmentInput, TicketSentAs } from '../types';
import type { ProofreadContext } from '../services/TicketProofreadService';

interface TicketReplyComposerProps {
  ticketId: string;
  /** Antwort-Empfaenger ("An"): Absender der juengsten eingehenden Nachricht; Fallback customer.email. */
  defaultToEmail: string;
  /** CC-Liste fuer "Allen antworten": uebrige Mitempfaenger der Ursprungsmail, ohne eigene @xtax.de-Adressen und den Verfasser. */
  replyAllCcEmails: string[];
  actor: TicketActor;
  /** Callback nach erfolgreichem Submit (Parent laedt das Ticket neu). */
  onSent: () => void;
}

type ComposerMode = 'reply' | 'note';
type ReplyScope = 'reply' | 'reply_all';

export const TicketReplyComposer: React.FC<TicketReplyComposerProps> = ({
  ticketId,
  defaultToEmail,
  replyAllCcEmails,
  actor,
  onSent
}) => {
  const [mode, setMode] = useState<ComposerMode>('reply');
  const [replyScope, setReplyScope] = useState<ReplyScope>('reply');
  const [toEmail, setToEmail] = useState(defaultToEmail);
  const [ccEmail, setCcEmail] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');
  const [attachments, setAttachments] = useState<TicketAttachmentInput[]>([]);
  const [attaching, setAttaching] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [scheduleMode, setScheduleMode] = useState<'now' | 'later'>('now');
  const [scheduledLocal, setScheduledLocal] = useState('');
  // Ob aktuell ein nicht uebernommener KI-Vorschlag offen ist (vom ProofreadButton
  // gemeldet) — fuer die Rueckfrage vor dem Senden.
  const [proofreadPending, setProofreadPending] = useState(false);

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  // Absender-Auswahl „Senden als andere Person" (nur Antwort-Modus relevant):
  // alle aktiven Mitarbeiter ohne Foto-Pflicht (requirePhoto=false — die
  // Ticket-Signatur hat einen Text-Fallback ohne Foto). Das Laden ist bewusst
  // auf eine aufgelöste Actor-E-Mail gegated, damit das Smart-Matching des
  // Hooks sicher den eingeloggten Nutzer als Default trifft und nicht — im
  // kurzen Ladefenster direkt nach Seitenaufruf — still auf den
  // Primär-Mitarbeiter zurückfällt.
  const actorEmailReady = Boolean(actor.staff_email && actor.staff_email.trim());
  const {
    senders,
    selectedSender,
    setSelectedSender,
    loading: sendersLoading,
    error: sendersError
  } = useSenderManagement(actorEmailReady, actor.staff_email, false);

  // Prüft, ob ein Sender die handelnde Person selbst ist — primär per Staff-ID,
  // ersatzweise per E-Mail (falls die Staff-Auflösung des Actors ausnahmsweise
  // scheiterte). Nur bei Abweichung wird sent_as gesetzt.
  const isSelectedSenderSelf = (sender: EmailSenderIdentity): boolean => {
    if (actor.staff_id && sender.id === actor.staff_id) return true;
    const actorEmail = (actor.staff_email || '').trim().toLowerCase();
    return !!actorEmail && (sender.email || '').trim().toLowerCase() === actorEmail;
  };

  // Aufgeloeste Fallback-Empfaengeradresse (Stufe 2 Kontakt / Stufe 3 letzte Inbound),
  // falls am Kunden keine E-Mail (customers.email = Stufe 1) hinterlegt ist. Wird beim
  // Mount einmalig ermittelt und ueberlebt resetForm.
  const resolvedFallbackToRef = React.useRef<string>('');

  React.useEffect(() => {
    // Stufe 1 liegt synchron als defaultToEmail vor. Nur wenn diese leer ist, die
    // Fallback-Adresse nachladen.
    if (defaultToEmail.trim()) return;

    let cancelled = false;
    (async () => {
      const resolved = await ticketService.resolveFallbackRecipientEmail(ticketId);
      if (cancelled || !resolved) return;
      resolvedFallbackToRef.current = resolved;
      // Bereits getippte Eingabe nicht ueberschreiben.
      setToEmail(prev => (prev.trim() ? prev : resolved));
    })();

    return () => { cancelled = true; };
  }, [ticketId, defaultToEmail]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    setAttaching(true);
    try {
      const newAttachments: TicketAttachmentInput[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const att = await fileToAttachmentInput(file);
        if (att) newAttachments.push(att);
      }
      setAttachments(prev => [...prev, ...newAttachments]);
    } catch (err) {
      console.error('[TicketReplyComposer] Fehler beim Lesen der Anhaenge:', err);
      alert('Anhang konnte nicht gelesen werden.');
    } finally {
      setAttaching(false);
      // Input zuruecksetzen, damit dieselbe Datei erneut ausgewaehlt werden kann
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (index: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== index));
  };

  // Umschalter "Antworten" / "Allen antworten": setzt die CC-Vorbelegung.
  // Antworten -> nur Verfasser (CC leer). Allen antworten -> uebrige Mitempfaenger.
  const applyReplyScope = (scope: ReplyScope) => {
    setReplyScope(scope);
    setCcEmail(scope === 'reply_all' ? replyAllCcEmails.join(', ') : '');
  };

  const resetForm = () => {
    setBodyHtml('');
    setAttachments([]);
    setScheduleMode('now');
    setScheduledLocal('');
    if (mode === 'reply') {
      setToEmail(defaultToEmail || resolvedFallbackToRef.current);
      setReplyScope('reply');
      setCcEmail('');
      // Außenidentität nicht über den nächsten Entwurf hinweg „kleben" lassen:
      // nach dem Senden zurück auf den eingeloggten Nutzer (sofern in der Liste).
      const self = senders.find(s => isSelectedSenderSelf(s));
      if (self) setSelectedSender(self);
    }
  };

  const handleSubmit = async () => {
    // Absender muss feststehen, bevor etwas gespeichert oder versendet wird.
    // Im kurzen Ladefenster direkt nach dem Seitenaufruf kann der Actor noch keine
    // E-Mail tragen (currentStaff bzw. user erst nach dem ersten Render aufgeloest).
    // Liefe der Submit dann durch, wuerde die Nachricht zwar gespeichert, der
    // anschliessende Mailversand aber mit "actor_email ist erforderlich"
    // fehlschlagen. Daher hier blockieren und den Nutzer kurz darauf erneut senden
    // lassen — keine halb ausgefuehrte Aktion.
    if (!actor.staff_email || !actor.staff_email.trim()) {
      alert('Benutzerdaten werden noch geladen. Bitte einen Moment warten und erneut senden.');
      return;
    }

    const { bodyHtml: cleanHtml, bodyText: plainText, inlineImages } = prepareEmailHtml(bodyHtml);

    // Rueckfrage, wenn ein KI-Vorschlag erzeugt, aber nicht uebernommen wurde:
    // sonst ginge versehentlich der unkorrigierte Originaltext raus. Gilt fuer
    // alle Versand-Wege und die interne Notiz.
    if (proofreadPending) {
      const proceed = window.confirm(
        'Es liegt ein nicht übernommener KI-Vorschlag vor. Wenn Du fortfährst, wird der unkorrigierte Originaltext verwendet. Trotzdem fortfahren?'
      );
      if (!proceed) return;
    }

    if (!plainText.trim()) {
      alert('Bitte einen Nachrichtentext eingeben.');
      return;
    }
    if (mode === 'reply') {
      const toTokens = parseEmails(toEmail);
      if (toTokens.length === 0) {
        alert('Empfänger-Adresse erforderlich für Antwort.');
        return;
      }
      const invalidTo = toTokens.find((addr) => !isValidEmail(addr));
      if (invalidTo) {
        alert(`Ungültige Empfänger-Adresse: „${invalidTo}". Bitte korrigieren oder entfernen.`);
        return;
      }
      const invalidCc = parseEmails(ccEmail).find((addr) => !isValidEmail(addr));
      if (invalidCc) {
        alert(`Ungültige CC-Adresse: „${invalidCc}". Bitte korrigieren oder entfernen.`);
        return;
      }
    }

    const isScheduled = mode === 'reply' && scheduleMode === 'later';
    if (isScheduled && !scheduledLocal) {
      alert('Bitte einen Sendezeitpunkt wählen.');
      return;
    }

    // Außenidentität „Senden als": nur gesetzt, wenn im Antwort-Modus ein vom
    // Actor abweichender Absender gewählt wurde. Ohne Auswahl (z. B. wenn die
    // Absender-Liste nicht geladen werden konnte) wird als man selbst
    // gesendet — Bestandsverhalten.
    const sentAs: TicketSentAs | undefined =
      mode === 'reply' && selectedSender && !isSelectedSenderSelf(selectedSender)
        ? { staff_id: selectedSender.id, name: selectedSender.fullName }
        : undefined;

    setSubmitting(true);
    try {
      if (isScheduled) {
        const scheduleResult = await ticketService.scheduleReply({
          ticket_id: ticketId,
          body_text: plainText,
          body_html: cleanHtml,
          to_emails: parseEmails(toEmail),
          cc_emails: ccEmail.trim() ? parseEmails(ccEmail) : undefined,
          scheduled_at_local: scheduledLocal,
          attachments: attachments.length > 0 ? attachments : undefined,
          inline_images: inlineImages.length > 0 ? inlineImages : undefined,
          sent_as: sentAs
        }, actor);

        if (!scheduleResult.success) {
          alert(`Senden konnte nicht geplant werden: ${scheduleResult.error}`);
          return;
        }

        resetForm();
        onSent();
        return;
      }

      const result = await ticketService.addMessage({
        ticket_id: ticketId,
        body_text: plainText,
        body_html: cleanHtml,
        is_internal_note: mode === 'note',
        send_email: mode === 'reply',
        to_emails: mode === 'reply' ? parseEmails(toEmail) : undefined,
        cc_emails: mode === 'reply' && ccEmail.trim() ? parseEmails(ccEmail) : undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        inline_images: inlineImages.length > 0 ? inlineImages : undefined,
        sent_as: sentAs
      }, actor);

      if (!result.success) {
        alert(`Nachricht konnte nicht gespeichert werden: ${result.error}`);
        return;
      }

      if (result.mail_error) {
        alert(`Nachricht gespeichert, aber Mail-Versand fehlgeschlagen: ${result.mail_error}`);
      }

      resetForm();
      onSent();
    } finally {
      setSubmitting(false);
    }
  };

  // Stellt den Kontext fuer die KI-Pruefung zusammen. Im Notiz-Modus ohne
  // Verlauf-Abruf (nur Rechtschreibung); im Antwort-Modus mit vollem Verlauf
  // (ticketService.getProofreadContext), der vom Service fertig formulierten
  // Anrede (salutation), dem Absendernamen und den aktuell angehaengten
  // Dateinamen. Wird vom ProofreadButton zum Pruefzeitpunkt aufgerufen.
  // „Senden als": Anrede/Duz-Gate und Grußformel-Name richten sich im
  // Antwort-Modus nach dem GEWÄHLTEN Absender (E-Mail bzw. Name); ohne Auswahl
  // nach dem Actor — Bestandsverhalten (leer → null → konservativ Sie).
  // is_scheduled_send meldet dem Server, ob gerade ein terminierter Versand
  // eingestellt ist; er unterdrückt dann die Freitags-Grußformel.
  const getProofreadContext = async (): Promise<ProofreadContext> => {
    const attachmentNames = attachments.map(a => a.filename);
    const geplanterVersand = mode === 'reply' && scheduleMode === 'later';
    if (mode === 'note') {
      return { requester_name: null, salutation: null, sender_name: actor.staff_full_name ?? null, is_scheduled_send: false, attachments: attachmentNames, thread: [] };
    }
    const identityEmail = selectedSender?.email && selectedSender.email.trim()
      ? selectedSender.email.trim()
      : (actor.staff_email && actor.staff_email.trim() ? actor.staff_email : null);
    const senderName = selectedSender?.fullName ?? actor.staff_full_name ?? null;
    const base = await ticketService.getProofreadContext(ticketId, identityEmail);
    return {
      requester_name: base.requester_name,
      salutation: base.salutation,
      sender_name: senderName,
      is_scheduled_send: geplanterVersand,
      attachments: attachmentNames,
      thread: base.thread
    };
  };

  const isBodyEmpty = isEmailHtmlEmpty(bodyHtml);
  const isNote = mode === 'note';
  const containerBg = isNote ? '#FFFBEB' : 'white';
  const containerBorder = isNote ? '#FDE68A' : '#E5E7EB';
  const submitColor = isNote ? '#D97706' : '#2563EB';
  const isScheduledMode = mode === 'reply' && scheduleMode === 'later';
  // E-Mail-Validierung der Empfaengerfelder (nur im Antwort-Modus, wo versendet wird).
  const toInvalid = mode === 'reply' && toEmail.trim().length > 0 && parseEmails(toEmail).some((addr) => !isValidEmail(addr));
  const ccInvalid = mode === 'reply' && ccEmail.trim().length > 0 && parseEmails(ccEmail).some((addr) => !isValidEmail(addr));
  const submitDisabled = submitting || attaching || isBodyEmpty || (isScheduledMode && !scheduledLocal) || toInvalid || ccInvalid;
  const submitLabel = mode === 'reply'
    ? (isScheduledMode ? 'Senden planen' : 'Antwort senden')
    : 'Notiz speichern';

  return (
    <div style={{
      marginTop: '20px',
      backgroundColor: containerBg,
      border: `1px solid ${containerBorder}`,
      borderRadius: '8px',
      padding: '14px 16px'
    }}>
      {/* Modus-Toggle */}
      <div style={{
        display: 'flex',
        gap: '4px',
        marginBottom: '14px',
        backgroundColor: 'rgba(0,0,0,0.04)',
        padding: '4px',
        borderRadius: '6px',
        width: 'fit-content'
      }}>
        <ModeButton
          label="Antwort an Kunde"
          icon={<Reply size={14} />}
          active={mode === 'reply'}
          onClick={() => setMode('reply')}
          accent="#2563EB"
        />
        <ModeButton
          label="Interne Notiz"
          icon={<StickyNote size={14} />}
          active={mode === 'note'}
          onClick={() => setMode('note')}
          accent="#D97706"
        />
      </div>

      {/* Empfaenger-Felder (nur im Antwort-Modus) */}
      {mode === 'reply' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          {/* Absender-Auswahl: „Senden als andere Person" */}
          <SenderSelection
            senders={senders}
            selectedSender={selectedSender}
            onSenderChange={setSelectedSender}
            disabled={submitting}
            loading={sendersLoading}
            error={sendersError}
          />
          {/* Antworten / Allen antworten - nur wenn es weitere Mitempfaenger gibt */}
          {replyAllCcEmails.length > 0 && (
            <div style={{
              display: 'inline-flex',
              backgroundColor: 'rgba(0,0,0,0.04)',
              borderRadius: '6px',
              padding: '3px',
              width: 'fit-content'
            }}>
              <button
                type="button"
                onClick={() => applyReplyScope('reply')}
                disabled={submitting}
                style={scheduleSegmentStyle(replyScope === 'reply')}
              >
                <Reply size={13} />
                Antworten
              </button>
              <button
                type="button"
                onClick={() => applyReplyScope('reply_all')}
                disabled={submitting}
                style={scheduleSegmentStyle(replyScope === 'reply_all')}
              >
                <ReplyAll size={13} />
                Allen antworten
              </button>
            </div>
          )}
          <LabeledInput
            label="An"
            value={toEmail}
            onChange={setToEmail}
            placeholder="empfänger@example.com (mehrere mit Komma trennen)"
            invalid={toInvalid}
          />
          <LabeledInput
            label="CC"
            value={ccEmail}
            onChange={setCcEmail}
            placeholder="optional"
            invalid={ccInvalid}
          />
        </div>
      )}

      {/* Body-Editor (Rich-Text) */}
      <RichTextEmailEditor
        value={bodyHtml}
        onChange={setBodyHtml}
        placeholder={mode === 'reply' ? 'Antwort schreiben...' : 'Interne Notiz (nur für XTAX-Team sichtbar)...'}
        proofreadMode={mode === 'note' ? 'note' : 'full'}
        getProofreadContext={getProofreadContext}
        onProofreadPendingChange={setProofreadPending}
      />

      {/* Anhaenge-Liste */}
      {attachments.length > 0 && (
        <div style={{
          marginTop: '10px',
          display: 'flex',
          flexDirection: 'column',
          gap: '6px'
        }}>
          {attachments.map((att, idx) => (
            <AttachmentItem
              key={`${att.filename}-${idx}`}
              filename={att.filename}
              fileSize={att.file_size}
              onRemove={() => handleRemoveAttachment(idx)}
              disabled={submitting}
            />
          ))}
        </div>
      )}

      {/* Toolbar: Anhang + (Antwort:) Sofort/Geplant + Senden/Planen */}
      <div style={{
        marginTop: '12px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '12px',
        flexWrap: 'wrap'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={attaching || submitting}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              backgroundColor: 'white',
              border: '1px solid #D1D5DB',
              borderRadius: '6px',
              fontSize: '13px',
              color: '#374151',
              cursor: attaching || submitting ? 'not-allowed' : 'pointer',
              opacity: attaching || submitting ? 0.6 : 1
            }}
          >
            {attaching ? (
              <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
            ) : (
              <Paperclip size={14} />
            )}
            Anhang
          </button>

          {mode === 'reply' && (
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              <div style={{
                display: 'inline-flex',
                backgroundColor: 'rgba(0,0,0,0.04)',
                borderRadius: '6px',
                padding: '3px'
              }}>
                <button
                  type="button"
                  onClick={() => setScheduleMode('now')}
                  disabled={submitting}
                  style={scheduleSegmentStyle(scheduleMode === 'now')}
                >
                  Sofort
                </button>
                <button
                  type="button"
                  onClick={() => setScheduleMode('later')}
                  disabled={submitting}
                  style={scheduleSegmentStyle(scheduleMode === 'later')}
                >
                  <Clock size={13} />
                  Geplant
                </button>
              </div>
              {scheduleMode === 'later' && (
                <input
                  type="datetime-local"
                  value={scheduledLocal}
                  min={berlinNowLocalString()}
                  onChange={(e) => setScheduledLocal(e.target.value)}
                  disabled={submitting}
                  style={{
                    padding: '6px 8px',
                    border: '1px solid #D1D5DB',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#374151',
                    backgroundColor: 'white'
                  }}
                />
              )}
            </div>
          )}
        </div>

        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitDisabled}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '8px 16px',
            backgroundColor: submitDisabled ? '#9CA3AF' : submitColor,
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            fontSize: '14px',
            fontWeight: '500',
            cursor: submitDisabled ? 'not-allowed' : 'pointer'
          }}
        >
          {submitting ? (
            <Loader size={14} style={{ animation: 'spin 1s linear infinite' }} />
          ) : isScheduledMode ? (
            <Clock size={14} />
          ) : (
            <Send size={14} />
          )}
          {submitLabel}
        </button>
      </div>
    </div>
  );
};

// ============================================
// Mode-Button
// ============================================

interface ModeButtonProps {
  label: string;
  icon: React.ReactNode;
  active: boolean;
  onClick: () => void;
  accent: string;
}

const ModeButton: React.FC<ModeButtonProps> = ({ label, icon, active, onClick, accent }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '6px 12px',
      backgroundColor: active ? 'white' : 'transparent',
      color: active ? accent : '#6B7280',
      border: 'none',
      borderRadius: '4px',
      fontSize: '13px',
      fontWeight: active ? '600' : '500',
      cursor: 'pointer',
      boxShadow: active ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
    }}
  >
    {icon}
    {label}
  </button>
);

// ============================================
// LabeledInput (kleines Feld mit Inline-Label)
// ============================================

interface LabeledInputProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  invalid?: boolean;
}

const LabeledInput: React.FC<LabeledInputProps> = ({ label, value, onChange, placeholder, invalid }) => (
  <div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <label style={{
        fontSize: '12px',
        fontWeight: '600',
        color: '#6B7280',
        minWidth: '32px'
      }}>
        {label}
      </label>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        style={{
          flex: 1,
          padding: '6px 10px',
          border: `1px solid ${invalid ? '#DC2626' : '#D1D5DB'}`,
          borderRadius: '6px',
          fontSize: '13px',
          backgroundColor: 'white'
        }}
      />
    </div>
    {invalid && (
      <div style={{ fontSize: '12px', color: '#DC2626', marginTop: '4px', marginLeft: '40px' }}>
        Ungültige E-Mail-Adresse.
      </div>
    )}
  </div>
);

// ============================================
// Attachment-Item (Liste der ausgewaehlten Anhaenge)
// ============================================

interface AttachmentItemProps {
  filename: string;
  fileSize: number;
  onRemove: () => void;
  disabled: boolean;
}

const AttachmentItem: React.FC<AttachmentItemProps> = ({ filename, fileSize, onRemove, disabled }) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '6px 10px',
    backgroundColor: 'white',
    border: '1px solid #E5E7EB',
    borderRadius: '6px',
    fontSize: '13px'
  }}>
    <Paperclip size={14} color="#6B7280" style={{ flexShrink: 0 }} />
    <span style={{
      flex: 1,
      color: '#374151',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap'
    }}>
      {filename}
    </span>
    <span style={{ color: '#9CA3AF', flexShrink: 0 }}>
      {formatFileSize(fileSize)}
    </span>
    <button
      type="button"
      onClick={onRemove}
      disabled={disabled}
      style={{
        background: 'none',
        border: 'none',
        padding: '2px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        color: '#9CA3AF',
        display: 'inline-flex',
        alignItems: 'center'
      }}
      title="Anhang entfernen"
    >
      <X size={14} />
    </button>
  </div>
);

// ============================================
// Helpers
// ============================================

/**
 * Liest eine Datei in base64 und gibt sie als TicketAttachmentInput zurueck.
 */
async function fileToAttachmentInput(file: File): Promise<TicketAttachmentInput | null> {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        resolve(null);
        return;
      }
      // result ist "data:<type>;base64,<base64>"
      const commaIdx = result.indexOf(',');
      const base64 = commaIdx >= 0 ? result.substring(commaIdx + 1) : '';
      resolve({
        filename: file.name,
        content_base64: base64,
        content_type: file.type || 'application/octet-stream',
        file_size: file.size
      });
    };
    reader.onerror = () => resolve(null);
    reader.readAsDataURL(file);
  });
}

/**
 * Zerlegt eine kommagetrennte Empfaengerliste in ein Array.
 * Filtert leere Eintraege und trimmt.
 */
function parseEmails(input: string): string[] {
  return input.split(',').map(s => s.trim()).filter(Boolean);
}

/** Format-Pruefung einer einzelnen E-Mail-Adresse (genau ein @, keine Leerzeichen, Punkt in der Domain). */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(email: string): boolean {
  return EMAIL_REGEX.test(email.trim());
}

function formatFileSize(bytes: number): string {
  if (!bytes || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / Math.pow(1024, exponent);
  return `${value.toFixed(value >= 10 || exponent === 0 ? 0 : 1)} ${units[exponent]}`;
}

/**
 * Aktuelle Berliner Wanduhrzeit als "YYYY-MM-DDTHH:mm" fuer das min-Attribut des
 * datetime-local-Felds (verhindert UI-seitig die Auswahl offensichtlich
 * vergangener Zeitpunkte; die eigentliche Pruefung passiert serverseitig).
 */
function berlinNowLocalString(): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  }).formatToParts(new Date());
  const m: Record<string, string> = {};
  for (const p of parts) {
    if (p.type !== 'literal') m[p.type] = p.value;
  }
  return `${m.year}-${m.month}-${m.day}T${m.hour}:${m.minute}`;
}

/** Style fuer die Sofort/Geplant-Segmentschalter. */
function scheduleSegmentStyle(active: boolean): React.CSSProperties {
  return {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    padding: '5px 10px',
    backgroundColor: active ? 'white' : 'transparent',
    color: active ? '#2563EB' : '#6B7280',
    border: 'none',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: active ? 600 : 500,
    cursor: 'pointer',
    boxShadow: active ? '0 1px 2px 0 rgba(0,0,0,0.06)' : 'none'
  };
}
